import AdminOnly from "common/AdminOnly";
import fetchJSON from "lib/fetchJSON";
import { getCurrentUser } from "lib/utils";
import React from "react";
import MultiRouteEmbed from "../embed/MultiRouteEmbed";
import { TripRouteEmbed } from "../embed/TripRouteEmbed";
// TODO account for styling changes due to missing bootstrap CSS
import styles from "./EmbedConfigurator.scss";

/*
 * You may notice that in development, the embed will flicker when the first
 * edit is made to it. This is the result of some async aspect of the
 * hot reloading code modifying the prototypes of the Embed components
 * after they are first rendered, causing react to remount them on the
 * subsequent update. The issue is not present in production.
 */
export default class EmbedConfigurator extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      title: props.title || "",
      description: props.description || "",
      metricUnits: !!props.metricUnits,
      sampleGraph: !!props.sampleGraph,
      defaultShowAll: !!props.defaultShowAll,
      brandedLogoPath: null,
    };
  }

  componentDidMount() {
    try {
      const metricUnits = !!window.rwgps.ns("config").currentUser.metric_units;
      fetchJSON(`/users/${getCurrentUser().id}/branded_embed_logo.json`).then((r) => {
        r.path && this.setState({ brandedLogoPath: r.path });
      });
      this.setState({ metricUnits });
    } catch (e) {
      // ¯\_(ツ)_/¯
    }
  }

  changeTitle = (e) => {
    this.setState({ title: e.target.value.slice(0, 80) });
  };

  changeDescription = (e) => {
    this.setState({ description: e.target.value.slice(0, 80) });
  };

  toggleUnits = (e) => {
    this.setState({ metricUnits: e.target.checked });
  };

  changeSampleGraph = (e) => {
    this.setState({ sampleGraph: e.target.checked });
  };

  changeDefaultShowAll = (e) => {
    this.setState({ defaultShowAll: e.target.checked });
  };

  iframeHeight() {
    const { sampleGraph } = this.state;
    let height = 550;
    if (sampleGraph) height += 150;
    return height;
  }

  embedIframe(iframeOpts = { includeHot: false }) {
    const { eventId, type, id, hot, privacyCode } = this.props;
    const { title, description, metricUnits, sampleGraph, defaultShowAll, brandedLogoPath } = this.state;
    const params = {
      type,
      eventId,
      id,
      title,
      description,
      metricUnits,
      sampleGraph,
      defaultShowAll,
      privacyCode,
      brandedLogoPath,
    };
    if (iframeOpts.includeHot) {
      params.hot = hot;
    }
    const iframeStyle = `width: 1px; min-width: 100%; height: ${this.iframeHeight()}px; border: none;`;
    const query = Object.keys(params)
      .filter((key) => params[key])
      .map((key) => `${key}=${encodeURIComponent(params[key])}`)
      .join("&");
    const domain = window.location.host === "ridewithgps.com" ? "https://ridewithgps.com" : window.location.origin;
    return `<iframe src="${domain}/embeds?${query}" style="${iframeStyle}" scrolling="no"></iframe>`;
  }

  displayTestFrame = () => {
    const iframe = this.embedIframe({ includeHot: true });
    const w = window.open();
    w.document.open();
    w.document.write(iframe);
    w.document.close();
  };

  render() {
    const { eventId, id, type, privacyCode } = this.props;
    const { title, description, metricUnits, sampleGraph, defaultShowAll, brandedLogoPath } = this.state;
    return (
      <div className={styles.EmbedConfigurator}>
        <div className={styles.LeftPanel}>
          <h1>Customize your embed</h1>
          <form>
            <BootstrapInput label="Title" value={title} onChange={this.changeTitle} />
            {type === "event" && (
              <BootstrapInput label="Description" value={description} onChange={this.changeDescription} />
            )}
            <BootstrapCheckbox label="Use metric units" value={metricUnits} onChange={this.toggleUnits} />
            <BootstrapCheckbox label="Show elevation" value={sampleGraph} onChange={this.changeSampleGraph} />
            {type === "event" && (
              <BootstrapCheckbox
                label='Default to "show all"'
                value={defaultShowAll}
                onChange={this.changeDefaultShowAll}
              />
            )}
            {defaultShowAll && (
              <p>
                Warning: using the default show all option may cause your website to load more slowly for embeds with
                many routes.
              </p>
            )}

            <label>Embed Code</label>
            <pre>
              <textarea
                className={styles.embedCode}
                readOnly
                value={this.embedIframe()}
                onClick={(e) => {
                  e.target.select();
                }}
              />
            </pre>
          </form>
          <AdminOnly>
            <a onClick={this.displayTestFrame}>test iframe</a>
          </AdminOnly>
        </div>
        <div className={styles.Preview}>
          <h2>Preview:</h2>
          <div style={{ height: `${this.iframeHeight()}px` }}>
            {(type === "event" || !type) && (
              <MultiRouteEmbed
                defaultShowAll={defaultShowAll}
                eventId={eventId}
                title={title}
                description={description}
                metricUnits={metricUnits}
                sampleGraph={sampleGraph}
              />
            )}
            {(type === "trip" || type === "route") && (
              <TripRouteEmbed
                privacyCode={privacyCode}
                id={id}
                type={type}
                title={title}
                description={description}
                metricUnits={metricUnits}
                sampleGraph={sampleGraph}
                brandedLogoPath={brandedLogoPath}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

EmbedConfigurator.defaultProps = { sampleGraph: true, type: "event" };

function BootstrapInput({ id, label, placeholder, onChange, value }) {
  // You should probably supply an id.
  const _id = id || Math.random().toString(36).substring(5);

  return (
    <div className="form-group">
      <label htmlFor={_id}>{label}</label>
      <input
        id={_id}
        className="form-control"
        type="text"
        onChange={onChange}
        placeholder={placeholder || label}
        value={value}
      />
    </div>
  );
}

function BootstrapCheckbox({ id, label, onChange, value }) {
  return (
    <div className="checkbox">
      <label>
        <input type="checkbox" checked={value} onChange={onChange} />
        {label}
      </label>
    </div>
  );
}
